import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/layout/AppShell";
import { Button, Badge, Card, Spinner } from "../components/common/UIKit";
import SearchableDropdown from "../components/common/SearchableDropdown";
import ConfirmModal from "../components/common/ConfirmModal";
import { LeadTypeCard } from "./PriceConfigurePage";
import {
  listLeadTypes, listSubscriptions, claimSession, claimPendingSubscriptions,
  createBulkCheckout, cancelSubscription, cancelLeadType, prorationPreview,
  getBillingPeriod,
} from "../api/subscriptions";
import { listCounties, listStates } from "../api/counties";
import { getSettings } from "../api/settings";
import { Plus, X, Zap, ArrowRight } from "lucide-react";
import { errMsg } from "../lib/utils";
import { formatPrice, readPricing, getLeadTypePrice, calcLeadTypeTotal } from "../utils/pricing";

const fallbackLeadTypes = [
  { name: "fsbo", label: "FSBO" },
  { name: "probate", label: "Probate" },
  { name: "nod", label: "NOD" },
  { name: "tax_liens", label: "Tax Liens" },
  { name: "divorce", label: "Divorce" },
  { name: "expired", label: "Expired" },
  { name: "canceled", label: "Canceled" },
  { name: "withdrawn", label: "Withdrawn" },
];

function calcSubtotal(name, count, pricing) {
  return calcLeadTypeTotal(name, count, pricing);
}

export default function SubscriptionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [countyOptions, setCountyOptions] = useState([]);
  const [leadTypesMeta, setLeadTypesMeta] = useState([]);
  const [subs, setSubs] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [lockedBilling, setLockedBilling] = useState(null);
  const [selectedState, setSelectedState] = useState("");
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedAddLeadTypes, setSelectedAddLeadTypes] = useState([]);
  const [addLtCounties, setAddLtCounties] = useState({});
  const [pendingCountyAdds, setPendingCountyAdds] = useState({});
  const [confirmAction, setConfirmAction] = useState(null);
  const [checkoutCanceled, setCheckoutCanceled] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [prorationModal, setProrationModal] = useState(null);
  const [prorationError, setProrationError] = useState(null);

  const refreshActive = async () => setSubs(await listSubscriptions());

  useEffect(() => {
    setCheckoutLoading(false);
    const resetRedirect = () => setCheckoutLoading(false);
    window.addEventListener("pageshow", resetRedirect);
    return () => window.removeEventListener("pageshow", resetRedirect);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [lt, settingsResp] = await Promise.all([
          listLeadTypes().catch(() => fallbackLeadTypes),
          getSettings().catch(() => null),
        ]);
        setLeadTypesMeta(lt?.length ? lt : fallbackLeadTypes);
        setSettings(settingsResp);
        const bp = await getBillingPeriod().catch(() => ({ billing: null }));
        if (bp?.billing) { setLockedBilling(bp.billing); setBilling(bp.billing); }
        await refreshActive();
      } finally {
        setInitialLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      (async () => {
        try {
          await claimSession(sessionId);
          toast.success("Subscription activated!");
          await refreshActive();
          setPendingCountyAdds({});
        } catch {
          try {
            const r = await claimPendingSubscriptions();
            if (r?.activated > 0) {
              toast.success("Subscription activated!");
              await refreshActive();
              setPendingCountyAdds({});
            }
          } catch {
          }
        }
      })();
      searchParams.delete("session_id");
      setSearchParams(searchParams);
    } else {
      claimPendingSubscriptions().then((r) => { if (r?.activated > 0) refreshActive(); }).catch(() => {});
    }
    const canceled = searchParams.get("canceled");
    const returned = searchParams.get("return");
    if (canceled) {
      setCheckoutLoading(false);
      setCheckoutCanceled(true);
      toast.info("Checkout canceled");
      searchParams.delete("canceled");
      setSearchParams(searchParams);
    } else if (returned && !sessionId) {
      setCheckoutLoading(false);
      setCheckoutCanceled(true);
      searchParams.delete("return");
      setSearchParams(searchParams);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pricing = useMemo(() => readPricing(settings || {}), [settings]);
  const { pricePerCounty, discountPercent } = pricing;
  const activeSubs = useMemo(() => subs.filter((s) => s.status === "active"), [subs]);

  const activeByLeadType = useMemo(() => {
    const map = new Map();
    for (const sub of activeSubs) {
      const name = sub.lead_type_name || (sub.is_all_types ? "all" : "");
      if (!name || name === "all") continue;
      if (!map.has(name)) {
        const meta = leadTypesMeta.find((lt) => lt.name === name) || { name, label: name };
        map.set(name, { ...meta, counties: [] });
      }
      map.get(name).counties.push(sub);
    }
    return Array.from(map.values()).map((row) => ({
      ...row,
      counties: row.counties.sort((a, b) => (a.county_name || "").localeCompare(b.county_name || "")),
    }));
  }, [activeSubs, leadTypesMeta]);

  const activeLeadTypeNames = useMemo(
    () => new Set(activeByLeadType.map((row) => row.name)),
    [activeByLeadType]
  );

  const currentState = activeSubs[0]?.state_code || selectedState;

  useEffect(() => {
    listStates().then(setStateOptions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!currentState) {
      setCountyOptions([]);
      return;
    }
    listCounties({ state_code: currentState })
      .then((resp) => setCountyOptions(resp.items || []))
      .catch(() => setCountyOptions([]));
  }, [currentState]);

  const availableLeadTypes = useMemo(
    () => leadTypesMeta.filter((lt) => !activeLeadTypeNames.has(lt.name)),
    [leadTypesMeta, activeLeadTypeNames]
  );
  const overallMonthlyTotal = activeByLeadType.reduce(
    (sum, row) => sum + calcSubtotal(row.name, row.counties.length, pricing),
    0
  );
  const countySelectOptions = useMemo(
    () => countyOptions.map((c) => ({ value: c.id, label: c.name })),
    [countyOptions]
  );
  const addSubtotals = useMemo(() => {
    const out = {};
    selectedAddLeadTypes.forEach((name) => {
      out[name] = calcSubtotal(name, (addLtCounties[name] || []).filter(Boolean).length, pricing);
    });
    return out;
  }, [selectedAddLeadTypes, addLtCounties, pricing]);
  const addMonthlyTotal = useMemo(
    () => Object.values(addSubtotals).reduce((sum, value) => sum + value, 0),
    [addSubtotals]
  );
  const addTotal = billing === "yearly"
    ? addMonthlyTotal * (1 - discountPercent / 100)
    : addMonthlyTotal;
  const canCheckoutAdd = selectedAddLeadTypes.length > 0 &&
    !!currentState &&
    selectedAddLeadTypes.every((name) => (addLtCounties[name] || []).some(Boolean));
  const activePendingRows = useMemo(
    () => Object.entries(pendingCountyAdds).flatMap(([name, counties]) => (counties || []).map((countyId) => ({ name, countyId }))),
    [pendingCountyAdds]
  );
  const hasPendingCountyAdds = activePendingRows.length > 0;
  const activePendingFilledRows = activePendingRows.filter((row) => row.countyId);
  const activePendingDelta = activePendingFilledRows.length * pricePerCounty;
  const canUpdateActivePlan = hasPendingCountyAdds && activePendingRows.every((row) => row.countyId);

  useEffect(() => {
    const available = new Set(availableLeadTypes.map((lt) => lt.name));
    setSelectedAddLeadTypes((prev) => {
      const next = prev.filter((name) => available.has(name));
      return next.length === prev.length ? prev : next;
    });
    setAddLtCounties((prev) => {
      const entries = Object.entries(prev).filter(([name]) => available.has(name));
      if (entries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(entries);
    });
  }, [availableLeadTypes]);

  const startCheckout = async (payload) => {
    const additions = (payload.lead_types || []).map((lt) => ({
      lead_type: lt.name || lt,
      counties: lt.counties || [],
    }));
    setProrationError(null);
    setProrationModal({ preview: null, payload, additions, loading: true });
    try {
      const preview = await prorationPreview({ additions, billing: payload.billing || "monthly" });
      setProrationModal((prev) => prev ? { ...prev, preview, loading: false } : null);
    } catch (e) {
      setProrationError(errMsg(e, "Could not calculate proration. Please try again."));
      setProrationModal((prev) => prev ? { ...prev, loading: false } : null);
    }
  };

  const confirmCheckout = async () => {
    if (!prorationModal?.payload) return;
    setCheckoutLoading(true);
    setProrationModal(null);
    try {
      const res = await createBulkCheckout(prorationModal.payload);
      window.location.href = res.session_url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
      setCheckoutLoading(false);
    }
  };

  const addPendingCountyRow = (name) => {
    setPendingCountyAdds((prev) => ({ ...prev, [name]: [...(prev[name] || []), ""] }));
  };

  const changePendingCounty = (name, idx, value) => {
    setPendingCountyAdds((prev) => {
      const next = { ...prev };
      const rows = [...(next[name] || [])];
      rows[idx] = value;
      next[name] = rows;
      return next;
    });
  };

  const removePendingCountyRow = (name, idx) => {
    setPendingCountyAdds((prev) => {
      const rows = (prev[name] || []).filter((_, i) => i !== idx);
      const next = { ...prev };
      if (rows.length) next[name] = rows;
      else delete next[name];
      return next;
    });
  };

  const updateActivePlan = () => {
    if (!canUpdateActivePlan) return;
    startCheckout({
      billing: "monthly",
      lead_types: Object.entries(pendingCountyAdds)
        .map(([name, counties]) => ({ name, counties: (counties || []).filter(Boolean) }))
        .filter((item) => item.counties.length > 0),
      total: activePendingDelta,
    });
  };

  const toggleAddLeadType = (name) => {
    setSelectedAddLeadTypes((prev) => {
      if (prev.includes(name)) {
        setAddLtCounties((current) => {
          const next = { ...current };
          delete next[name];
          return next;
        });
        return prev.filter((item) => item !== name);
      }
      setAddLtCounties((current) => ({ ...current, [name]: [""] }));
      return [...prev, name];
    });
  };

  const addNewCountySlot = (name) => {
    setAddLtCounties((prev) => ({ ...prev, [name]: [...(prev[name] || [""]), ""] }));
  };

  const removeNewCountySlot = (name, idx) => {
    setAddLtCounties((prev) => ({ ...prev, [name]: (prev[name] || []).filter((_, i) => i !== idx) }));
  };

  const changeNewCounty = (name, idx, value) => {
    setAddLtCounties((prev) => {
      const next = { ...prev };
      const slots = [...(next[name] || [""])];
      slots[idx] = value;
      next[name] = slots;
      return next;
    });
  };

  const checkoutAddedLeadTypes = () => {
    if (!canCheckoutAdd) return;
    startCheckout({
      billing,
      lead_types: selectedAddLeadTypes.map((name) => ({
        name,
        counties: (addLtCounties[name] || []).filter(Boolean),
      })),
      total: addTotal,
    });
  };

  const removeCounty = async (sub) => {
    try {
      await cancelSubscription(sub.id);
      toast.success("County removed");
      await refreshActive();
    } catch (e) {
      toast.error(errMsg(e, "Could not remove county"));
    }
  };

  const removeCountyFromLeadType = async (sub, row) => {
    if (row.counties.length === 1) {
      setConfirmAction({
        title: `Cancel ${row.label}?`,
        message: `This will remove ${row.label} and all its counties from your plan. This action cannot be undone.`,
        onConfirm: () => removeCounty(sub),
      });
      return;
    }
    await removeCounty(sub);
  };

  const cancelType = async (row) => {
    try {
      await cancelLeadType(row.name);
      toast.success("Lead type canceled");
      await refreshActive();
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel lead type"));
    }
  };

  const requestCancelType = (row) => {
    setConfirmAction({
      title: `Cancel ${row.label}?`,
      message: `This will remove ${row.label} and all its counties from your plan. This action cannot be undone.`,
      onConfirm: () => cancelType(row),
    });
  };

  const confirmAndClose = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    await action?.onConfirm?.();
  };

  if (checkoutLoading) {
    return (
      <AppShell>
        <div className="min-h-[calc(100vh-100px)] flex flex-col items-center justify-center gap-3" data-testid="subs-checkout-loading">
          <Spinner />
          <p className="text-sm font-semibold text-zinc-950">Setting up your subscription...</p>
          <p className="text-xs text-zinc-500">Redirecting to Stripe</p>
        </div>
      </AppShell>
    );
  }

  if (initialLoading) {
    return (
      <AppShell title="Subscriptions" subtitle="Manage your plan and counties.">
        <div className="min-h-[calc(100vh-180px)] flex flex-col items-center justify-center gap-3 px-8" data-testid="subs-page-loading">
          <Spinner />
          <p className="text-sm font-semibold text-zinc-950">Loading your subscription data...</p>
          <p className="text-xs text-zinc-500">Preparing your plan details and county access</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Subscriptions" subtitle="Manage your plan and counties.">
      <div className="w-full max-w-5xl mx-auto p-4 sm:p-6 md:p-8 space-y-6">
        {checkoutCanceled && (
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm font-semibold text-zinc-700" data-testid="subs-checkout-canceled-banner">
            Checkout was cancelled. Your plan has not changed.
          </div>
        )}

        {activeByLeadType.length > 0 ? (
          <Card className="border-2 border-zinc-950 bg-zinc-950 text-white" data-testid="current-plan-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">Your plan</p>
                <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="current-plan-name">
                  Per Lead Type
                </h2>
                <p className="text-sm text-zinc-300 mt-1">
                  {activeByLeadType.length} lead type{activeByLeadType.length === 1 ? "" : "s"} active
                </p>
              </div>
              <Zap className="h-8 w-8 text-green-400" strokeWidth={2.5} />
            </div>
          </Card>
        ) : (
          <Card className="border-2 border-dashed border-zinc-300 text-center py-6" data-testid="no-plan-card">
            <h2 className="text-lg font-bold text-zinc-950 mb-1">No active subscription yet</h2>
            <p className="text-sm text-zinc-500">Add a lead type below, then checkout to start.</p>
          </Card>
        )}

        {activeByLeadType.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="active-subs-list">
            {activeByLeadType.map((row) => {
              const pendingRows = pendingCountyAdds[row.name] || [];
              const pendingFilledCount = pendingRows.filter(Boolean).length;
              const subtotal = calcSubtotal(row.name, row.counties.length + pendingFilledCount, pricing);
              const usedCountyIds = new Set([
                ...row.counties.map((sub) => sub.county_id),
                ...pendingRows.filter(Boolean),
              ]);
              return (
                <Card key={row.name} data-testid={`subs-lead-type-card-${row.name}`}>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-xl font-black tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {row.label}
                      </h2>
                      <p className="text-sm text-zinc-500">{row.counties.length} {row.counties.length === 1 ? "county" : "counties"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-black text-zinc-950">{formatPrice(subtotal)}<span className="text-xs text-zinc-400">/mo</span></p>
                      <button type="button" onClick={() => requestCancelType(row)} className="text-xs font-semibold text-red-600 hover:underline" data-testid={`subs-cancel-lt-${row.name}`}>
                        Cancel type
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {row.counties.map((sub, idx) => (
                      <div key={sub.id} className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2" data-testid={`active-sub-${sub.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-zinc-950 truncate">{sub.county_name}</p>
                          <p className="text-xs text-zinc-500">{sub.state}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {idx === 0 ? <Badge color="green">INCLUDED</Badge> : <Badge color="amber">+{formatPrice(pricePerCounty)}/MO</Badge>}
                          <button type="button" onClick={() => removeCountyFromLeadType(sub, row)} className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50" data-testid={`subs-remove-county-${sub.id}`}>
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {pendingRows.map((countyId, idx) => {
                      const available = countyOptions
                        .filter((c) => !usedCountyIds.has(c.id) || c.id === countyId)
                        .map((c) => ({ value: c.id, label: c.name }));
                      return (
                        <div key={`pending-${idx}`} className="flex items-center justify-between gap-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2" data-testid={`subs-pending-county-${row.name}-${idx}`}>
                          <div className="flex-1 min-w-0">
                            <SearchableDropdown
                              testId={`subs-pending-county-select-${row.name}-${idx}`}
                              value={countyId}
                              onChange={(v) => changePendingCounty(row.name, idx, v)}
                              options={available}
                              placeholder="Select county"
                              searchPlaceholder="Search counties..."
                              clearable={false}
                              disabled={!currentState}
                            />
                          </div>
                          <Badge color="zinc">NEW</Badge>
                          <button type="button" onClick={() => removePendingCountyRow(row.name, idx)} className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50" data-testid={`subs-remove-pending-county-${row.name}-${idx}`}>
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => addPendingCountyRow(row.name)}
                    data-testid={`subs-add-county-${row.name}`}
                    className="w-full flex items-center justify-center gap-1 text-xs font-semibold text-zinc-500 hover:text-zinc-950 border border-dashed border-zinc-200 hover:border-zinc-400 rounded-md py-2 transition-colors"
                  >
                    <Plus className="h-3 w-3" /> Add county
                  </button>
                </Card>
              );
            })}
          </div>
        )}

        {hasPendingCountyAdds && (
          <Card data-testid="subs-active-update-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-zinc-950">Unsaved county additions</p>
                <p className="text-xs text-zinc-500">
                  {activePendingFilledRows.length} filled of {activePendingRows.length} pending {activePendingRows.length === 1 ? "county" : "counties"}
                </p>
              </div>
              <Button data-testid="subs-update-plan-button" disabled={!canUpdateActivePlan} onClick={updateActivePlan}>
                Update plan · +{formatPrice(activePendingDelta)}/mo <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </Card>
        )}

        {availableLeadTypes.length > 0 ? (
          <Card data-testid="add-counties-card">
            <div className="flex items-baseline justify-between mb-1">
              <h2 className="text-xl font-black tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Add lead types
              </h2>
            </div>
            <p className="text-sm text-zinc-500 mb-5" data-testid="subs-new-lead-type-price">
              Select one or more lead types to add. Each includes 1 county.
            </p>

            <div className="flex items-center gap-2 mb-5" data-testid="subs-billing-toggle">
              <div className="inline-flex items-center p-1 rounded-full border border-zinc-200 bg-white">
                {lockedBilling !== "yearly" && (
                  <button type="button" data-testid="subs-billing-monthly" onClick={() => setBilling("monthly")} className={`px-4 py-1 text-xs font-bold rounded-full transition-colors ${billing === "monthly" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-950"}`}>
                    Monthly
                  </button>
                )}
                {lockedBilling !== "monthly" && (
                  <button type="button" data-testid="subs-billing-yearly" onClick={() => setBilling("yearly")} className={`px-4 py-1 text-xs font-bold rounded-full flex items-center gap-1.5 transition-colors ${billing === "yearly" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-950"}`}>
                    Yearly
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${billing === "yearly" ? "bg-green-400 text-zinc-950" : "bg-green-50 text-green-700"}`}>
                      -{discountPercent}%
                    </span>
                  </button>
                )}
              </div>
            </div>

            {activeSubs.length === 0 ? (
              <div className="mb-4">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">State</p>
                <SearchableDropdown
                  testId="subs-state-select"
                  value={selectedState}
                  onChange={(v) => {
                    setSelectedState(v);
                    setAddLtCounties((prev) => Object.fromEntries(Object.keys(prev).map((k) => [k, [""]])));
                  }}
                  options={stateOptions.map((s) => ({ value: s.state_code, label: s.state }))}
                  placeholder="Select a state"
                  searchPlaceholder="Search states..."
                />
              </div>
            ) : (
              <div className="mb-3">
                <Badge color="zinc" className="font-semibold">{activeSubs[0]?.state}</Badge>
                <span className="text-sm text-zinc-500 ml-2">Active state</span>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="subs-new-lead-types">
              {availableLeadTypes.map((lt) => {
                const on = selectedAddLeadTypes.includes(lt.name);
                return (
                  <label
                    key={lt.name}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm font-semibold cursor-pointer transition-colors ${on ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-950"}`}
                    data-testid={`subs-new-lt-${lt.name}`}
                  >
                    <span className="flex items-center gap-2">
                      <input type="checkbox" checked={on} onChange={() => toggleAddLeadType(lt.name)} className="h-3.5 w-3.5 accent-zinc-950" />
                      {lt.label}
                    </span>
                    <span className={`text-xs font-bold ${on ? "text-zinc-300" : "text-zinc-400"}`}>{formatPrice(getLeadTypePrice(lt.name, pricing))}/mo</span>
                  </label>
                );
              })}
            </div>

            {selectedAddLeadTypes.length > 0 && !currentState && (
              <p className="text-xs text-amber-600 font-semibold mb-3">← Select a state to pick counties</p>
            )}

            {selectedAddLeadTypes.length > 0 && (
              <div className="space-y-3 mb-4" data-testid="subs-new-lead-type-cards">
                {selectedAddLeadTypes.map((name) => {
                  const lt = availableLeadTypes.find((item) => item.name === name);
                  return (
                    <LeadTypeCard
                      key={name}
                      leadType={lt}
                      counties={addLtCounties[name] || [""]}
                      countyOptions={countySelectOptions}
                      onAddCounty={addNewCountySlot}
                      onRemoveCounty={removeNewCountySlot}
                      onChangeCounty={changeNewCounty}
                      pricing={pricing}
                    />
                  );
                })}
              </div>
            )}

            {selectedAddLeadTypes.length > 0 && (
              <div className="bg-zinc-50 rounded-md border border-zinc-200 p-4 mb-4" data-testid="subs-add-summary">
                <div className="space-y-2">
                  {selectedAddLeadTypes.map((name) => {
                    const lt = availableLeadTypes.find((item) => item.name === name);
                    const counties = (addLtCounties[name] || []).filter(Boolean);
                    return (
                      <div key={name} className="grid grid-cols-[7rem_1fr_auto] gap-3 text-sm" data-testid={`subs-add-summary-${name}`}>
                        <span className="font-semibold text-zinc-950">{lt?.label}</span>
                        <span className="text-zinc-600">
                          {counties.length
                            ? counties.map((id, idx) => {
                              const county = countyOptions.find((c) => c.id === id);
                              return `${county?.name || id}${idx === 0 ? " (included)" : ` +${formatPrice(pricePerCounty)}`}`;
                            }).join(", ")
                            : "Select at least 1 county"}
                        </span>
                        <span className="font-semibold text-zinc-950">{formatPrice(addSubtotals[name] || 0)}/mo</span>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t border-zinc-200 mt-3 pt-3 flex items-baseline justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Total</span>
                  <div className="text-right">
                    <span className="text-xl font-black text-zinc-950" data-testid="subs-add-total">{formatPrice(addTotal)}</span>
                    <span className="text-sm text-zinc-500">/mo</span>
                    {billing === "yearly" && (
                      <p className="text-xs font-semibold text-green-700" data-testid="subs-new-lead-type-effective-price">
                        {formatPrice(addTotal)}/mo effective (billed yearly)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <Button data-testid="subs-checkout-button" className="w-full h-11" disabled={!canCheckoutAdd} onClick={checkoutAddedLeadTypes}>
                Checkout <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Card>
        ) : (
          <Card data-testid="subs-all-lead-types-message">
            <p className="text-sm font-semibold text-zinc-700">You're subscribed to all available lead types.</p>
          </Card>
        )}

        {/* <Card data-testid="subs-summary">
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Overall total</span>
            <div className="text-right">
              <span className="text-4xl font-black tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="subs-total">
                {formatPrice(overallMonthlyTotal)}
              </span>
              <span className="text-sm text-zinc-500">/mo</span>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-1 text-right" data-testid="subs-breakdown">
            {activeByLeadType.length > 0 ? `${activeByLeadType.length} active lead type${activeByLeadType.length === 1 ? "" : "s"}` : "No active subscription"}
          </p>
        </Card> */}
      </div>
      {confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          message={confirmAction.message}
          cancelLabel="Keep plan"
          confirmLabel="Yes, cancel type"
          confirmVariant="danger"
          onCancel={() => setConfirmAction(null)}
          onConfirm={confirmAndClose}
        />
      )}
      {prorationModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          onClick={() => setProrationModal(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="proration-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <h2 className="text-sm font-bold text-zinc-950">Confirm plan update</h2>
              <button type="button" onClick={() => setProrationModal(null)} className="h-6 w-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-950 hover:bg-zinc-100">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {prorationError ? (
                <div className="rounded-md bg-red-50 border border-red-200 p-3">
                  <p className="text-sm text-red-700">{prorationError}</p>
                  <button
                    type="button"
                    onClick={() => { setProrationError(null); startCheckout(prorationModal.payload); }}
                    className="mt-2 text-xs font-semibold text-red-700 underline"
                  >
                    Retry
                  </button>
                </div>
              ) : !prorationModal.preview ? (
                <div className="flex items-center justify-center py-6">
                  <Spinner />
                  <span className="ml-2 text-sm text-zinc-500">Calculating proration...</span>
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Adding</p>
                    {(prorationModal.payload.lead_types || []).map((lt) => {
                      const name = lt.name || lt;
                      const ltMeta = leadTypesMeta.find((m) => m.name === name);
                      const counties = lt.counties || [];
                      return counties.map((cId, i) => {
                        const county = countyOptions.find((c) => c.id === cId);
                        return (
                          <div key={`${name}-${i}`} className="flex items-center justify-between text-sm">
                            <span className="text-zinc-700">{ltMeta?.label || name} → {county?.name || cId}</span>
                            {i === 0
                              ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">INCLUDED</span>
                              : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">+{formatPrice(pricePerCounty)}/mo</span>
                            }
                          </div>
                        );
                      });
                    })}
                  </div>
                  <div className="border-t border-zinc-100 pt-3 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">Charged today (prorated)</span>
                      <span className="font-bold text-zinc-950" data-testid="proration-charged-today">
                        {prorationModal.preview.charged_today === 0 ? "Nothing charged today" : formatPrice(prorationModal.preview.charged_today)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">From {prorationModal.preview.next_billing_date || "next cycle"} onwards</span>
                      <span className="font-semibold text-zinc-700">+{formatPrice(prorationModal.preview.next_cycle_addition)}/mo</span>
                    </div>
                    {prorationModal.preview.charged_today > 0 && prorationModal.preview.next_billing_date && (() => {
                      const d = new Date(prorationModal.preview.next_billing_date).getDate();
                      const ordinal = (n) => {
                        if (n > 3 && n < 21) return n + 'th';
                        switch (n % 10) { case 1: return n + 'st'; case 2: return n + 'nd'; case 3: return n + 'rd'; default: return n + 'th'; }
                      };
                      return <p className="text-xs text-zinc-400 pt-1">All future charges are billed on the {ordinal(d)} of each month.</p>;
                    })()}
                  </div>
                </>
              )}
            </div>
            {!prorationError && prorationModal.preview && (
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-100">
                <button type="button" onClick={() => setProrationModal(null)} className="h-9 px-4 rounded-md border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-zinc-950">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmCheckout}
                  disabled={checkoutLoading}
                  className="h-9 px-4 rounded-md bg-zinc-950 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-60"
                  data-testid="proration-confirm-button"
                >
                  {checkoutLoading
                    ? "Processing..."
                    : prorationModal.preview.charged_today === 0
                      ? "Confirm changes"
                      : `Confirm & pay ${formatPrice(prorationModal.preview.charged_today)}`
                  }
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}
